export type MatchedByType =
  | 'manual'
  | 'deterministic'
  | 'semantic_auto'
  | 'semantic_fallback'
  | 'ai_confirmed'
  | 'ai_pending'
  | 'keyword_pending'
  | 'keyword_confirmed';

/**
 * Closed vocabulary for `liveStatus`. Mirrors the canonical source in
 * `arbitrage-core/services/prediction-pair-manager/src/live-events/live-events.constants.ts`
 * — redeclared here because the FE is a separate yarn workspace and we don't
 * pull a cross-repo dependency for two strings. Every check in the FE MUST
 * go through these constants so a typo is a compile error, not a silent
 * mismatch that hides the pair from the Live tab. See feedback rule
 * `enum-vocabularies-use-named-constants` in memory.
 */
export const LIVE_STATUS_LIVE = 'live' as const;
export const LIVE_STATUS_IDLE = 'idle' as const;
export type LiveStatus = typeof LIVE_STATUS_LIVE | typeof LIVE_STATUS_IDLE;

export interface PredictionOpportunity {
  pairId: string;
  pairName: string;
  direction: OpportunityDirection;
  cost: number;
  profitAbs: number;
  profitPct: number;
  daysToExpiry: number;
  makerTna: number;
  takerTna: number;
  ear: number;
  maxFillableUsd: number;
  maxFillableContracts: number;
  matchedBy: MatchedByType;
  matchConfidence?: number;
  polarityInverted?: boolean;
  correlationType?: 'exact' | 'correlated';
  keywordOverlap?: string[];
  timestamp: number;
  validUntil: number;
  category?: string;
  archived?: boolean;
  /** Live-events classifier output. 'live' → pair belongs to the Live sub-tab
   *  on /predictions/opportunities (Phase 2). Default 'idle'. See the
   *  live-events skill for the contract. */
  liveStatus?: LiveStatus;
}

export interface OpportunityDirection {
  yesPlatform: string;
  noPlatform: string;
  yesAsk: number;
  noAsk: number;
  yesTitle: string;
  noTitle: string;
  yesMarketUrl?: string;
  noMarketUrl?: string;
  // Expiry date the calculator takes for each platform's market. Per-side
  // because Polymarket and Kalshi can report different close times for the
  // same question. Always present: the calculator falls back to the pair-
  // level expiryDate (required) when both per-site fields are missing, so
  // the Days hover never degrades to "unknown".
  yesExpiryDate: string;
  noExpiryDate: string;
}

// WebSocket response types
export interface PredictionSnapshotResponse {
  type: 'SNAPSHOT';
  data: PredictionOpportunity[];
  count: number;
  timestamp: number;
}

export interface PredictionUpdateResponse {
  type: 'UPDATE';
  data: PredictionOpportunity[];
  count: number;
  timestamp: number;
}

export interface PredictionCleanupAction {
  type: 'CLEANUP';
}

export interface PredictionRemovePairAction {
  type: 'REMOVE_PAIR';
  pairId: string;
}

export interface PredictionConfirmPairAction {
  type: 'CONFIRM_PAIR';
  pairId: string;
  matchedBy: MatchedByType;
}

export interface PredictionDeltaUpdateAction {
  type: 'DELTA_UPDATE';
  opportunity: PredictionOpportunity;
  timestamp: number;
}

export interface PredictionDeltaRemoveAction {
  type: 'DELTA_REMOVE';
  pairId: string;
  timestamp: number;
}

export type PredictionDelta =
  | PredictionDeltaUpdateAction
  | PredictionDeltaRemoveAction;

export type WSPredictionResponse =
  | PredictionSnapshotResponse
  | PredictionUpdateResponse
  | PredictionCleanupAction
  | PredictionRemovePairAction
  | PredictionConfirmPairAction
  | PredictionDeltaUpdateAction
  | PredictionDeltaRemoveAction;

export type PredictionsLocalCache = Map<
  PredictionOpportunity['pairId'],
  PredictionOpportunity
>;

export interface PredictionPosition {
  _id: string;
  pairId: string | null;
  platform: 'polymarket' | 'kalshi';
  marketId: string;
  side: 'yes' | 'no';
  avgPrice: number;
  contracts: number;
  // Lifetime total bought (set on closed Polymarket legs from
  // /closed-positions). `contracts` is CURRENT holdings (= 0 for a
  // fully-sold leg); the Ended tab and the CLOSED chip on the Open tab
  // fall back to this for the historical size.
  totalBought?: number;
  currentPrice?: number;
  unrealizedPnl?: number;
  status: 'open' | 'closed' | 'expired';
  redeemable?: boolean;
  marketStatus?: string;
  claimStatus?: 'need_claim' | 'claimed';
  externalId?: string;
  marketUrl?: string;
  pairName?: string;
  topBid?: number;
  topAsk?: number;
  endDate?: string;
  lastSyncedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface PositionsGroupedByPair {
  _id: string | null;
  pairName: string;
  polymarketTitle?: string;
  kalshiTitle?: string;
  positions: PredictionPosition[];
  totalInvested: number;
  totalUnrealizedPnl: number;
  netContracts: number;
  hedgePnl: number | null;
  makerTna: number | null;
  takerTna: number | null;
  daysToExpiry: number | null;
  expiryDate?: string | null;
  tnaAlertsEnabled?: boolean;
  /**
   * Kalshi market settled on the live `market_lifecycle_v2` feed. When true the
   * positions/unwinder row shows a gray `TERMINADO` chip (mirrors the opener).
   * Display-only; independent of `eventFinished`.
   */
  settledOnKalshi?: boolean;
  settledAt?: string | null;
  kalshiResult?: 'yes' | 'no' | null;
}

export interface PerformanceData {
  initialValue: number;
  initialDate: string;
  kalshi: {
    balanceUsd: number;
    portfolioValueUsd: number;
    mainTotalUsd: number;
    testBalanceUsd: number;
    testPortfolioValueUsd: number;
    testTotalUsd: number;
    /** Grand total (main + test account) — feeds the "Kalshi" chip. */
    totalUsd: number;
  };
  polymarket: {
    positionsValueUsd: number;
    cashBalanceUsd: number;
    mainTotalUsd: number;
    testPositionsValueUsd: number;
    testCashBalanceUsd: number;
    testTotalUsd: number;
    /** Grand total (main + test account) — feeds the "Poly" chip. */
    totalUsd: number;
  };
  cashflows: {
    depositsUsd: number;
    withdrawalsUsd: number;
    netFlowUsd: number;
    byPlatform: {
      kalshi: { deposits: number; withdrawals: number };
      polymarket: { deposits: number; withdrawals: number };
    };
  };
  baselineValueUsd: number;
  currentValueUsd: number;
  pnlUsd: number;
}

export interface PredictionCashflow {
  _id: string;
  platform: 'kalshi' | 'polymarket';
  type: 'deposit' | 'withdrawal';
  amountUsd: number;
  note?: string;
  date: string;
  createdAt: string;
  updatedAt: string;
}
